import type React from 'react';
import { useEffect, useState } from 'react';
import { SKU_AUTOCOMPLETE_MAX_RESULTS } from '../constants/app.constants';

interface UseSkuAutocompleteParams {
  value: string;
  allSkus: string[];
  prefix?: string;
  skipAutocompleteOpenRef?: React.MutableRefObject<boolean>;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  autocompleteRef?: React.RefObject<HTMLDivElement | null>;
}

interface UseSkuAutocompleteResult {
  filteredSkus: string[];
  showAutocomplete: boolean;
  setShowAutocomplete: React.Dispatch<React.SetStateAction<boolean>>;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
}

export function useSkuAutocomplete({
  value,
  allSkus,
  prefix = 'RSV-',
  skipAutocompleteOpenRef,
  inputRef,
  autocompleteRef,
}: UseSkuAutocompleteParams): UseSkuAutocompleteResult {
  const [filteredSkus, setFilteredSkus] = useState<string[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    if (skipAutocompleteOpenRef?.current) {
      skipAutocompleteOpenRef.current = false;
      if (value.length > prefix.length) {
        const searchTerm = value.substring(prefix.length).toUpperCase();
        const filtered = allSkus
          .filter((sku) => sku.toUpperCase().includes(searchTerm))
          .slice(0, SKU_AUTOCOMPLETE_MAX_RESULTS);
        setFilteredSkus(filtered);
      }
      setSelectedIndex(-1);
      setShowAutocomplete(false);
      return;
    }

    if (value.length > prefix.length) {
      const searchTerm = value.substring(prefix.length).toUpperCase();
      const filtered = allSkus
        .filter((sku) => sku.toUpperCase().includes(searchTerm))
        .slice(0, SKU_AUTOCOMPLETE_MAX_RESULTS);
      setFilteredSkus(filtered);
      setShowAutocomplete(filtered.length > 0);
    } else {
      setFilteredSkus([]);
      setShowAutocomplete(false);
    }
    setSelectedIndex(-1);
  }, [value, allSkus, prefix, skipAutocompleteOpenRef]);

  useEffect(() => {
    if (showAutocomplete && inputRef?.current && autocompleteRef?.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const dropdown = autocompleteRef.current;
      dropdown.style.top = `${inputRect.bottom}px`;
      dropdown.style.left = `${inputRect.left}px`;
      dropdown.style.width = `${inputRect.width}px`;
    }
  }, [showAutocomplete, value, filteredSkus, inputRef, autocompleteRef]);

  return {
    filteredSkus,
    showAutocomplete,
    setShowAutocomplete,
    selectedIndex,
    setSelectedIndex,
  };
}
